import React from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { ImageWithFallback } from '../components/media/ImageWithFallback';

function About() {
    const volunteers = [
        { name: 'Dr. Sarah Johnson', role: 'Medical Director', icon: 'mdi:doctor' },
        { name: 'Prof. Michael Chen', role: 'Education Lead', icon: 'mdi:school' },
        { name: 'Aisha Mohammed', role: 'Community Outreach', icon: 'mdi:account-group' },
        { name: 'David Williams', role: 'Research Coordinator', icon: 'mdi:flask' }
    ];

    const values = [
        {
            icon: 'mdi:heart',
            title: 'Compassion',
            description: 'We care deeply about the communities we serve and approach every initiative with empathy.'
        },
        {
            icon: 'mdi:shield-check',
            title: 'Integrity',
            description: 'We maintain the highest ethical standards in all our operations and partnerships.'
        },
        {
            icon: 'mdi:lightbulb',
            title: 'Innovation',
            description: 'We embrace creative solutions and cutting-edge approaches to solve complex challenges.'
        },
        {
            icon: 'mdi:account-multiple',
            title: 'Collaboration',
            description: 'We work together with communities, partners, and stakeholders to achieve shared goals.'
        },
        {
            icon: 'mdi:star',
            title: 'Excellence',
            description: 'We strive for the highest quality in everything we do, from program design to implementation.'
        },
        {
            icon: 'mdi:earth',
            title: 'Sustainability',
            description: 'We create lasting impact through programs designed for long-term community benefit.'
        }
    ];

    return (
        <div className="about-page">
            {/* Page Header */}
            <section className="page-header">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-6">
                            <h1 className="page-title animate-slide-up">About HSCF</h1>
                            <p className="page-subtitle animate-slide-up delay-1">
                                Learn about our journey, mission, and the people making a difference
                                in communities around the world.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Our Story */}
            <section className="story-section">
                <div className="container">
                    <div className="row align-items-start">
                        <div className="col-lg-6 mb-4 mb-lg-0">
                            <div className="story-image-wrapper">
                                <ImageWithFallback
                                    src="https://images.unsplash.com/photo-1573164574511-73c773193279?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=1169"
                                    alt="Our Story"
                                    className="story-image"
                                />
                            </div>
                        </div>
                        <div className="col-lg-6 px-4">
                            <div className="section-header">
                                <span className="section-label">Our Story</span>
                                {/*<h2 className="section-title">A Decade of Impact</h2>*/}
                            </div>
                            <p className="story-text">
                                The Health and Social Change Foundation (HSC Foundation) is an indigenous nonprofit organization dedicated to advancing the health and well-being of underserved communities, with a special focus on those living in remote and marginalized areas.
                            </p>
                            <p className="story-text">
                                We believe that true, positive, and sustainable change begins within communities when their knowledge, leadership, ownership, and dreams are tapped into. That's why we work with and through local actors to design and implement solutions that are rooted in local cultures, responsive to local needs, and adaptable to challenging geographic and social realities.
                            </p>
                            <p className="story-text">
                                At the same time, HSC Foundation serves as Nigeria's first white-label nonprofit platform, enabling individual and organizational donors and philanthropists to implement their own giving ideas through our trusted governance and compliance framework.
                            </p>
                            <p className="story-text">
                                Through this model, we bridge community-driven change with donor-driven innovation, ensuring that every idea, whether home-grown or globally inspired, is implemented with the highest level of transparency and accountability to achieve impact.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Mission & Vision */}
            <section className="mission-vision-section">
                <div className="container">
                    <div className="row g-4 justify-content-center">
                        <div className="col-lg-6">
                            <div className="mv-card mission-card">
                                <div className="mv-icon">
                                    <Icon icon="mdi:target" />
                                </div>
                                <h3>Our Objectives</h3>
                                <div className="mv-highlights">
                                    <div className="highlight-item">
                                        <Icon icon="mdi:check-circle" />
                                        <span>
                                            Providing a secure & trusted platform for inclusive philanthropy and giving.
                                        </span>
                                    </div>
                                    <div className="highlight-item">
                                        <Icon icon="mdi:check-circle" />
                                        <span>
                                            Partnering with local & global organizations and governments to implement SDGs.
                                        </span>
                                    </div>
                                    <div className="highlight-item">
                                        <Icon icon="mdi:check-circle" />
                                        <span>
                                            Conducting and supporting evidence-based research to inform policies and interventions.
                                        </span>
                                    </div>
                                    <div className="highlight-item">
                                        <Icon icon="mdi:check-circle" />
                                        <span>
                                            Mentoring and empowering young leaders through training and skills development initiatives.
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Core Values */}
            <section className="values-section">
                <div className="container">
                    <div className="section-header text-center">
                        <span className="section-label">Our Core Values</span>
                        <h2 className="section-title">What Guides Us</h2>
                        <p className="section-description">
                            Our core values shape every decision we make and every program we implement
                        </p>
                    </div>

                    <div className="row g-4">
                        {values.map((value, index) => (
                            <div key={index} className="col-lg-4 col-md-6">
                                <div className="value-card">
                                    <div className="value-icon">
                                        <Icon icon={value.icon} />
                                    </div>
                                    <h4>{value.title}</h4>
                                    <p>{value.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Volunteers & Fellows */}
            {/*
            <section className="volunteers-section">
                <div className="container">
                <div className="section-header text-center">
                    <span className="section-label">Our Team</span>
                    <h2 className="section-title">Volunteers & Fellows</h2>
                    <p className="section-description">
                    Meet some of the dedicated individuals driving our mission forward
                    </p>
                </div>

                <div className="row g-4">
                    {volunteers.map((volunteer, index) => (
                    <div key={index} className="col-lg-3 col-md-6">
                        <div className="volunteer-card">
                        <div className="volunteer-avatar">
                            <Icon icon={volunteer.icon} />
                        </div>
                        <h4>{volunteer.name}</h4>
                        <p>{volunteer.role}</p>
                        <div className="volunteer-social">
                            <a href="#" aria-label="LinkedIn">
                            <Icon icon="mdi:linkedin" />
                            </a>
                            <a href="#" aria-label="Twitter">
                            <Icon icon="mdi:twitter" />
                            </a>
                        </div>
                        </div>
                    </div>
                    ))}
                </div>

                <div className="volunteer-cta">
                    <h3>Want to Join Our Team?</h3>
                    <p>We're always looking for passionate individuals to volunteer with us</p>
                    <Link to="/contact" className="btn btn-volunteer">
                    <Icon icon="mdi:hand-heart" className="me-2" />
                    Become a Volunteer
                    </Link>
                </div>
                </div>
            </section>
            */}

            {/* Impact Timeline */}
            {/*
            <section className="timeline-section">
                <div className="container">
                <div className="section-header text-center">
                    <span className="section-label">Our Journey</span>
                    <h2 className="section-title">Impact Timeline</h2>
                </div>

                <div className="timeline">
                    <div className="timeline-item">
                    <div className="timeline-marker"></div>
                    <div className="timeline-content">
                        <h4>2015</h4>
                        <p>HSCF founded with first community health initiative</p>
                    </div>
                    </div>
                    <div className="timeline-item">
                    <div className="timeline-marker"></div>
                    <div className="timeline-content">
                        <h4>2017</h4>
                        <p>EducateHer program launched, supporting 100 young women</p>
                    </div>
                    </div>
                    <div className="timeline-item">
                    <div className="timeline-marker"></div>
                    <div className="timeline-content">
                        <h4>2019</h4>
                        <p>Expanded to 15 communities, 5,000+ lives impacted</p>
                    </div>
                    </div>
                    <div className="timeline-item">
                    <div className="timeline-marker"></div>
                    <div className="timeline-content">
                        <h4>2021</h4>
                        <p>RTYGIA and Aikpokpodion Fellowship programs established</p>
                    </div>
                    </div>
                    <div className="timeline-item">
                    <div className="timeline-marker"></div>
                    <div className="timeline-content">
                        <h4>2023</h4>
                        <p>Reached 25 communities, 10,000+ beneficiaries</p>
                    </div>
                    </div>
                    <div className="timeline-item">
                    <div className="timeline-marker"></div>
                    <div className="timeline-content">
                        <h4>2025</h4>
                        <p>Continuing to innovate and expand our impact</p>
                    </div>
                    </div>
                </div>
                </div>
            </section>
            */}
        </div>
    );
}

export default About;
